// components/reports/UnitsAnalyticsReport.js
import React, { useState, useEffect } from 'react';
import { getUnitsAnalytics } from '../../services/reportService';

/**
 * Компонент отчета по аналитике единиц измерения
 * @returns {JSX.Element} - Компонент отчета
 */
const UnitsAnalyticsReport = () => {
  const [data, setData] = useState({
    productWeightStats: [],
    productVolumeStats: [],
    priceTagOverrideCount: 0,
    overrideStats: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const analytics = await getUnitsAnalytics();
      setData(analytics);
    } catch (err) {
      setError('Не удалось загрузить аналитику по единицам измерения');
      console.error('Error loading units analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Получение названия единицы измерения на русском
  const getUnitDisplayName = (unitValue, type = 'weight') => {
    if (type === 'weight') {
      const weightUnits = {
        1: 'грамм',
        2: 'килограмм', 
        3: 'фунт',
        4: 'унция'
      };
      return weightUnits[unitValue] || 'неизвестная единица';
    } else {
      const volumeUnits = {
        1: 'миллилитр',
        2: 'литр',
        3: 'жидкая унция',
        4: 'галлон',
        5: 'кубический метр'
      };
      return volumeUnits[unitValue] || 'неизвестная единица';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <svg className="h-12 w-12 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-600">{error}</p>
            <button
              onClick={loadData}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Повторить попытку
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Аналитика единиц измерения</h2>
        <p className="text-gray-600">Статистика использования весовых и объемных единиц в системе</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Статистика весовых единиц */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Единицы измерения веса</h3>
          
          {data.productWeightStats.length === 0 ? (
            <div className="text-center py-8">
              <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8L9 9l4 4" />
              </svg>
              <p className="text-gray-500">Данные о весовых единицах отсутствуют</p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.productWeightStats.map((stat, index) => (
                <div key={index} className="border-l-4 border-blue-400 pl-4 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 capitalize">
                        {getUnitDisplayName(stat.unitValue, 'weight')}
                      </p>
                      <p className="text-sm text-gray-500">
                        Товаров: {stat.count} • Средний вес: {stat.avgWeight} {getUnitDisplayName(stat.unitValue, 'weight')}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {stat.count}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Статистика объемных единиц */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Единицы измерения объема</h3>
          
          {data.productVolumeStats.length === 0 ? (
            <div className="text-center py-8">
              <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8L9 9l4 4" />
              </svg>
              <p className="text-gray-500">Данные об объемных единицах отсутствуют</p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.productVolumeStats.map((stat, index) => (
                <div key={index} className="border-l-4 border-green-400 pl-4 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 capitalize">
                        {getUnitDisplayName(stat.unitValue, 'volume')}
                      </p>
                      <p className="text-sm text-gray-500">
                        Товаров: {stat.count} • Средний объем: {stat.avgVolume} {getUnitDisplayName(stat.unitValue, 'volume')}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        {stat.count}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Статистика переопределений в ценниках */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Переопределения в ценниках
          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {data.priceTagOverrideCount}
          </span>
        </h3>
        
        {data.priceTagOverrideCount === 0 ? (
          <div className="text-center py-8">
            <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500">Переопределения веса/объема в ценниках не найдены</p>
            <p className="text-xs text-gray-400 mt-2">
              Это означает, что все ценники используют стандартные характеристики товаров
            </p>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-yellow-800 font-medium">
                  Найдено {data.priceTagOverrideCount} ценников с переопределенными характеристиками
                </p>
                <p className="text-yellow-700 text-sm mt-1">
                  Некоторые ценники используют отличные от товара значения веса или объема. 
                  Это может быть полезно для анализа точности данных о товарах.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnitsAnalyticsReport;